<template>
  <section class="client-cabinet-plain-section" data-testid="client-page-details" aria-labelledby="client-page-details-title">
    <h2 id="client-page-details-title">{{ $t("client_page.cabinet.details.title") }}</h2>
    <div class="client-cabinet-summary-grid" data-testid="client-page-details-summary">
      <span>{{ $t("client_page.cabinet.details.pickup_at") }}</span>
      <strong data-field="booking.start_date">{{ wallDateTime(booking.start_date) }}</strong>
      <span>{{ $t("client_page.cabinet.details.return_at") }}</span>
      <strong data-field="booking.end_date">{{ wallDateTime(booking.end_date) }}</strong>
      <span>{{ $t("client_page.cabinet.details.vehicle") }}</span>
      <strong>{{ booking.car_name }}</strong>
    </div>
    <button
      type="button"
      class="client-cabinet-disclosure"
      data-testid="client-page-disclosure-details"
      :aria-expanded="String(showDetails)"
      aria-controls="client-page-details-content"
      @click="showDetails = !showDetails"
    >
      {{ $t(showDetails ? "client_page.cabinet.details.hide" : "client_page.cabinet.details.show") }}
    </button>
    <div id="client-page-details-content" v-show="showDetails" data-testid="client-page-details-content">
      <v-simple-table dense>
        <template v-slot:default>
          <!-- Пустые места выдачи и возврата не показываются (design, часть 2). -->
          <tbody
            data-field="company.rental_object booking.vseprokaty_id booking.fast_booking booking.location_start booking.location_end booking.chair booking.chairs_quantity booking.chair_less_1_year booking.booster booking.boosters_quantity booking.navigator booking.mp3 booking.charger booking.wifi_router booking.other_equipment booking.kasko booking.super_kasko booking.theft booking.no_franchise booking.in_rent booking.ride booking.clean_start booking.clean_end booking.gas_start booking.gas_end booking.gas_start_full booking.gas_end_full booking.start_mileage booking.end_mileage booking.booking_mileage"
          >
            <tr data-field="booking.created_at">
              <td>{{ $t("client_page.cabinet.details.created") }}</td>
              <td class="text-right">{{ formatDate(booking.created_at) }}</td>
            </tr>
            <tr v-if="booking.vseprokaty_id">
              <td>{{ $t("agregator.agregator_id") }}</td>
              <td class="text-right">{{ booking.vseprokaty_id }}</td>
            </tr>
            <tr v-if="booking.fast_booking">
              <td colspan="2">{{ $t("agregator.fast_booking") }}</td>
            </tr>
            <tr v-if="booking.location_start">
              <td>{{ $t("client_page.cabinet.details.pickup") }}</td>
              <td class="text-right">{{ booking.location_start }}</td>
            </tr>
            <tr v-if="booking.location_end">
              <td>{{ $t("client_page.cabinet.details.return") }}</td>
              <td class="text-right">{{ booking.location_end }}</td>
            </tr>

            <!-- Допы -->
            <template v-if="booking.chair || booking.chair_less_1_year || booking.booster || booking.navigator || booking.mp3 || booking.charger || booking.wifi_router || booking.other_equipment">
              <tr><th colspan="2">{{ $t("client_page.cabinet.details.extras") }}</th></tr>
              <tr v-if="booking.chair">
                <td>{{ $t("bookings.baby_chair") }}</td>
                <td class="text-right">{{ booking.chairs_quantity }}</td>
              </tr>
              <tr v-if="booking.chair_less_1_year">
                <td>{{ $t("bookings.small_baby_chair") }}</td>
                <td class="text-right">{{ booking.chairs_quantity }}</td>
              </tr>
              <tr v-if="booking.booster">
                <td>{{ $t("bookings.booster") }}</td>
                <td class="text-right">{{ booking.boosters_quantity }}</td>
              </tr>
              <tr v-if="booking.navigator"><td colspan="2">{{ $t("bookings.navigator") }}</td></tr>
              <tr v-if="booking.mp3"><td colspan="2">{{ $t("bookings.mp3") }}</td></tr>
              <tr v-if="booking.charger"><td colspan="2">{{ $t("bookings.charger") }}</td></tr>
              <tr v-if="booking.wifi_router"><td colspan="2">{{ $t("bookings.wifi") }}</td></tr>
              <tr v-if="booking.other_equipment"><td colspan="2">{{ booking.other_equipment }}</td></tr>
            </template>

            <!-- Страховки (FT-183: скрыты для типа техники компании) -->
            <template v-if="showInsurance && (booking.kasko || booking.super_kasko || booking.theft || booking.no_franchise)">
              <tr><th colspan="2">{{ $t("bookings.add_insurance") }}</th></tr>
              <tr v-if="booking.kasko"><td colspan="2">{{ $t("bookings.kasko") }}</td></tr>
              <tr v-if="booking.super_kasko"><td colspan="2">{{ $t("bookings.super_kasko") }}</td></tr>
              <tr v-if="booking.theft"><td colspan="2">{{ $t("bookings.theft_insurance") }}</td></tr>
              <tr v-if="booking.no_franchise"><td colspan="2">{{ $t("bookings.no_franchise") }}</td></tr>
            </template>

            <!-- Состояние при выдаче и приёмке -->
            <template v-if="booking.in_rent || booking.ride || (showMileage && (booking.start_mileage > 0 || booking.end_mileage > 0))">
              <tr><th colspan="2">{{ $t("bookings.car_condition") }}</th></tr>
              <tr v-if="booking.in_rent || booking.ride">
                <td>{{ $t("bookings.clean_start") }}</td>
                <td class="text-right">{{ booking.clean_start ? $t("client_page.cabinet.details.yes") : $t("client_page.cabinet.details.no") }}</td>
              </tr>
              <tr v-if="booking.ride">
                <td>{{ $t("bookings.clean_end") }}</td>
                <td class="text-right">{{ booking.clean_end ? $t("client_page.cabinet.details.yes") : $t("client_page.cabinet.details.no") }}</td>
              </tr>
              <!-- FT-183: топливо скрыто для типа техники компании -->
              <template v-if="showFuel">
                <tr v-if="(booking.in_rent || booking.ride) && !booking.gas_start_full">
                  <td>{{ $t("bookings.gas_start") }}</td>
                  <td class="text-right">{{ booking.gas_start }}</td>
                </tr>
                <tr v-if="(booking.in_rent || booking.ride) && booking.gas_start_full">
                  <td colspan="2">{{ $t("bookings.gas_start_full") }}</td>
                </tr>
                <tr v-if="!booking.gas_end_full && booking.ride">
                  <td>{{ $t("bookings.gas_end_short") }}</td>
                  <td class="text-right">{{ booking.gas_end }}</td>
                </tr>
                <tr v-if="booking.gas_end_full && booking.ride">
                  <td colspan="2">{{ $t("bookings.gas_end_full") }}</td>
                </tr>
              </template>
              <!-- FT-183: пробег скрыт для типа техники компании -->
              <template v-if="showMileage">
                <tr v-if="(booking.in_rent || booking.ride) && booking.start_mileage > 0">
                  <td>{{ $t("bookings.mileage_start") }}</td>
                  <td class="text-right">{{ booking.start_mileage }} {{ $t("custom.km") }}</td>
                </tr>
                <tr v-if="booking.end_mileage > 0">
                  <td>{{ $t("bookings.mileage_end") }}</td>
                  <td class="text-right">{{ booking.end_mileage }} {{ $t("custom.km") }}</td>
                </tr>
                <tr v-if="booking.booking_mileage > 0">
                  <td>{{ $t("bookings.booking_mileage") }}</td>
                  <td class="text-right">{{ booking.booking_mileage }} {{ $t("custom.km") }}</td>
                </tr>
                <tr v-if="booking.booking_mileage > 0 && booking.days > 0">
                  <td>{{ $t("bookings.average_mileage") }}</td>
                  <td class="text-right">{{ Math.trunc(booking.booking_mileage / booking.days) }} {{ $t("bookings.km_day") }}</td>
                </tr>
              </template>
            </template>
          </tbody>
        </template>
      </v-simple-table>

      <div data-field="booking.booking_photos_data">
        <booking-photo-gallery
          v-if="booking.booking_photos_data && booking.booking_photos_data.length"
          class="mt-3"
          :photos="booking.booking_photos_data"
        ></booking-photo-gallery>
      </div>
    </div>
  </section>
</template>

<script>
// FT-200 STEP-05 (REQ-08): свёрнутые «Детали» — места выдачи и возврата (пустые не показываются), допы, страховка,
// состояние при выдаче и приёмке, фото.
//
// Публичная страница: стора компании нет, тип техники — из ответа (company.rental_object). Локальный rental_object
// перекрывает миксин — иначе флаги FT-183 ушли бы на фолбэк "cars" для вело- и лодочных прокатов.
import moment from "moment";
import rentalObjectMixin from "@/mixins/rentalObjectMixin";
import BookingPhotoGallery from "../BookingPhotoGallery";
import { momentLocaleFor } from "@/utils/localeInfra";

export default {
  name: "DetailsBlock",
  components: { BookingPhotoGallery },
  mixins: [rentalObjectMixin],
  props: {
    booking: { type: Object, required: true },
    company: { type: Object, default: null },
  },
  data() {
    return { showDetails: false };
  },
  computed: {
    rental_object() {
      return (this.company && this.company.rental_object) || "cars";
    },
  },
  methods: {
    // Время получения и возврата — «стенное» время проката, как его ввёл менеджер: без перевода в пояс клиента.
    wallDateTime(value) {
      if (!value) return "";
      const date = moment.utc(value, "DD-MM-YYYY HH:mm", true);
      return date.isValid() ? date.locale(momentLocaleFor((this.$i18n && this.$i18n.locale) || "ru")).format("D MMMM, HH:mm") : value;
    },
    formatDate(date) {
      return date ? moment(date).locale(momentLocaleFor((this.$i18n && this.$i18n.locale) || "ru")).format("D MMMM, HH:mm") : "";
    },
  },
};
</script>
